import type { PromiseItem, SectionRow } from '@/types'

import { HeartHandshake } from 'lucide-react'

import Reveal from '@/components/landing/reveal'
import SectionHeading from '@/components/landing/section-heading'

interface PromisesSectionProps {
    section?: SectionRow
    promises: Array<PromiseItem>
}

const pad = (value: number): string => String(value).padStart(2, '0')

export default function PromisesSection({ section, promises }: PromisesSectionProps) {
    return (
        <section id='promises' className='relative overflow-hidden bg-gradient-to-b from-blush/70 via-[#fff4f6] to-cream py-24 sm:py-32'>
            <div aria-hidden className='absolute -top-24 right-10 size-80 rounded-full bg-white/70 blur-3xl' />

            <div className='relative mx-auto max-w-6xl px-6 lg:px-10'>
                <SectionHeading section={section} />

                <ol className='mx-auto mt-14 grid max-w-5xl gap-5 sm:mt-16 md:grid-cols-2'>
                    {promises.map((item, index) => (
                        <Reveal
                            key={item.id}
                            as='li'
                            delay={(index % 2) * 110}
                            className='md:[&:last-child:nth-child(odd)]:col-span-2 md:[&:last-child:nth-child(odd)]:mx-auto md:[&:last-child:nth-child(odd)]:w-1/2'
                        >
                            <div className='group flex h-full items-start gap-5 rounded-[1.75rem] bg-white/85 p-6 shadow-[0_20px_50px_-34px_rgba(91,21,48,0.6)] ring-1 ring-blush backdrop-blur transition duration-300 hover:-translate-y-1 hover:ring-petal/40 sm:p-7'>
                                <span className='font-heading text-4xl leading-none font-semibold text-petal/80 italic transition-colors group-hover:text-petal-deep'>
                                    {pad(index + 1)}
                                </span>

                                {item.promise && (
                                    <p
                                        data-mdlfy-cms={`promises:${item.id}:promise`}
                                        className='pt-1 font-heading text-xl leading-snug text-wine sm:text-[1.35rem]'
                                    >
                                        {item.promise}
                                    </p>
                                )}
                            </div>
                        </Reveal>
                    ))}
                </ol>

                <Reveal className='mt-14 flex justify-center' direction='zoom'>
                    <p className='inline-flex -rotate-3 items-center gap-3 rounded-full border-2 border-dashed border-petal-deep/45 px-7 py-3 text-sm font-bold tracking-[0.28em] text-petal-deep uppercase'>
                        <HeartHandshake aria-hidden className='size-5' />
                        Pakka promise
                    </p>
                </Reveal>
            </div>
        </section>
    )
}
